import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display", "form"]

  connect() {
    this.isSubmitting = false
  }

  showForm() {
    this.displayTarget.classList.add("hidden")
    this.formTarget.classList.remove("hidden")
    
    // Set up form submission listener when form is shown
    const form = this.formTarget.querySelector("form")
    if (form && !form._stimulusListenersAttached) {
      form.addEventListener("submit", this.handleSubmit.bind(this))
      form._stimulusListenersAttached = true
    }
    
    // Focus the first input field
    const firstInput = this.formTarget.querySelector("input, textarea, select")
    if (firstInput) {
      firstInput.focus()
    }
  }

  toggle() {
    if (this.formTarget.classList.contains("hidden")) {
      this.showForm()
    } else {
      this.hideForm()
    }
  }


  hideForm() {
    this.displayTarget.classList.remove("hidden")
    this.formTarget.classList.add("hidden")
  }

  cancel() {
    this.hideForm()
  }

  async handleSubmit(event) {
    event.preventDefault()
    
    // Prevent double submissions
    if (this.isSubmitting) {
      return
    }
    
    this.isSubmitting = true
    const form = event.target
    const submitButton = form.querySelector('input[type="submit"]')
    const originalText = submitButton.value

    // Show loading state
    submitButton.value = "Saving..."
    submitButton.disabled = true

    try {
      const formData = new FormData(form)
      const response = await fetch(form.action, {
        method: 'PATCH',
        body: formData,
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.status === 'success') {
        this.handleSuccess(data)
      } else {
        this.handleError(data)
      }
    } catch (error) {
      console.error('Error:', error)
      this.showNotification('An error occurred while saving', 'error')
    } finally {
      // Reset button state
      submitButton.value = originalText
      submitButton.disabled = false
      this.isSubmitting = false
    }
  }

  handleSuccess(data) {
    // Update the display content with the new data
    this.updateDisplayContent(data.organization)
    
    // Update the profile completion percentage
    this.updateProfileCompletion(data.completion_percentage)
    
    // Hide form and show display
    this.hideForm()
    
    // Show success notification
    this.showNotification(data.message || 'Saved successfully!', 'success')
  }

  handleError(data) {
    console.error('Save failed:', data)
    let errorMessage = 'Failed to save'
    
    if (data.errors) {
      const errorMessages = Object.values(data.errors).flat()
      errorMessage = errorMessages.join(', ')
    }
    
    this.showNotification(errorMessage, 'error')
  }

  updateDisplayContent(organization) {
    // Get the field being edited from the form
    const form = this.formTarget.querySelector('form')
    const formData = new FormData(form)
    
    // Find which field was updated by looking at the form inputs
    for (let [fieldName, fieldValue] of formData.entries()) {
      if (fieldName.startsWith('organization[') && fieldName.endsWith(']')) {
        const cleanFieldName = fieldName.replace('organization[', '').replace(']', '')
        
        // Update the display content based on the field type
        const displayElement = this.displayTarget
        
        if (cleanFieldName === 'description') {
          if (fieldValue && fieldValue.trim()) {
            displayElement.innerHTML = `
              <div class="prose prose-lg prose-blue max-w-none text-gray-700 leading-relaxed">
                ${fieldValue.replace(/\n/g, '<br>')}
              </div>
            `
          } else {
            displayElement.innerHTML = `
              <div class="text-center py-8">
                <div class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i class="fas fa-edit text-gray-400 text-xl"></i>
                </div>
                <p class="text-gray-600 mb-4">No description provided yet.</p>
              </div>
            `
          }
        } else if (cleanFieldName === 'specialties') {
          if (fieldValue && fieldValue.trim()) {
            const specialties = fieldValue.split(',').map(s => s.trim()).filter(s => s)
            displayElement.innerHTML = `
              <div class="flex flex-wrap gap-3">
                ${specialties.map(specialty => `
                  <span class="inline-flex items-center px-4 py-2 rounded-full bg-blue-600 text-white font-medium text-sm hover:bg-blue-700 transition-colors cursor-default">
                    <i class="fas fa-check-circle mr-2"></i>
                    ${specialty}
                  </span>
                `).join('')}
              </div>
            `
          } else {
            displayElement.innerHTML = `
              <div class="text-center py-6">
                <div class="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i class="fas fa-star text-blue-600"></i>
                </div>
                <p class="text-gray-600 mb-3">No specialties listed yet</p>
              </div>
            `
          }
        } else if (cleanFieldName === 'service_areas') {
          if (fieldValue && fieldValue.trim()) {
            const areas = fieldValue.split(',').map(s => s.trim()).filter(s => s)
            displayElement.innerHTML = `
              <div class="flex flex-wrap gap-3">
                ${areas.map(area => `
                  <span class="inline-flex items-center px-4 py-2 rounded-full bg-emerald-600 text-white font-medium text-sm hover:bg-emerald-700 transition-colors cursor-default">
                    <i class="fas fa-location-dot mr-2"></i>
                    ${area}
                  </span>
                `).join('')}
              </div>
            `
          } else {
            displayElement.innerHTML = `
              <div class="text-center py-6">
                <div class="w-12 h-12 bg-emerald-200 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i class="fas fa-map-marker-alt text-emerald-600"></i>
                </div>
                <p class="text-gray-600 mb-3">No service areas listed yet</p>
              </div>
            `
          }
        } else if (cleanFieldName === 'certifications') {
          if (fieldValue && fieldValue.trim()) {
            const certs = fieldValue.split(',').map(s => s.trim()).filter(s => s)
            displayElement.innerHTML = `
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                ${certs.map(cert => `
                  <div class="flex items-center p-4 bg-white rounded-xl shadow-sm border border-amber-200 hover:shadow-md transition-shadow">
                    <div class="w-8 h-8 bg-amber-600 rounded-lg flex items-center justify-center mr-3">
                      <i class="fas fa-award text-white text-sm"></i>
                    </div>
                    <span class="font-medium text-gray-900">${cert}</span>
                  </div>
                `).join('')}
              </div>
            `
          } else {
            displayElement.innerHTML = `
              <div class="text-center py-8">
                <div class="w-16 h-16 bg-amber-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i class="fas fa-certificate text-amber-600 text-2xl"></i>
                </div>
                <p class="text-gray-600 mb-4">No certifications listed yet</p>
              </div>
            `
          }
        } else if (cleanFieldName === 'business_email' || cleanFieldName === 'phone' || cleanFieldName === 'website' || cleanFieldName === 'address' || cleanFieldName === 'city' || cleanFieldName === 'state') {
          // For contact information fields, we need to reconstruct the entire contact info display
          this.updateContactInformation()
        } else if (cleanFieldName === 'founded_year' || cleanFieldName === 'employee_count' || cleanFieldName === 'business_license' || cleanFieldName === 'business_hours') {
          // For business details fields, we need to reconstruct the entire business details display
          this.updateBusinessDetails()
        } else if (fieldName.includes('social_links_hash')) {
          // For social media fields, we need to reconstruct the entire social links display
          this.updateSocialLinks()
        } else {
          // Generic text update for other fields
          displayElement.textContent = fieldValue || 'Not specified'
        }
        break
      }
    }
  }

  updateContactInformation() {
    // Get current form data
    const form = this.formTarget.querySelector('form')
    const formData = new FormData(form)
    
    // Extract values
    const businessEmail = formData.get('organization[business_email]') || ''
    const phone = formData.get('organization[phone]') || ''
    const website = formData.get('organization[website]') || ''
    const address = formData.get('organization[address]') || ''
    const city = formData.get('organization[city]') || ''
    const state = formData.get('organization[state]') || ''
    
    // Build full address
    let fullAddress = ''
    if (address) {
      fullAddress = address
      if (city || state) {
        fullAddress += ', '
        if (city) fullAddress += city
        if (city && state) fullAddress += ', '
        if (state) fullAddress += state
      }
    }
    
    // Build the HTML structure
    let contactHtml = '<div class="space-y-6">'
    
    // Address
    if (fullAddress) {
      contactHtml += `
        <div class="flex items-start">
          <div class="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-4 mt-1">
            <i class="fas fa-map-marker-alt text-sm"></i>
          </div>
          <div>
            <div class="font-medium text-indigo-100">Address</div>
            <div class="text-white">${fullAddress}</div>
          </div>
        </div>
      `
    }
    
    // Phone
    if (phone) {
      contactHtml += `
        <div class="flex items-center">
          <div class="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-4">
            <i class="fas fa-phone text-sm"></i>
          </div>
          <div>
            <div class="font-medium text-indigo-100">Phone</div>
            <a href="tel:${phone}" class="text-white hover:text-indigo-200 transition-colors">
              ${phone}
            </a>
          </div>
        </div>
      `
    }
    
    // Email
    if (businessEmail) {
      contactHtml += `
        <div class="flex items-center">
          <div class="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-4">
            <i class="fas fa-envelope text-sm"></i>
          </div>
          <div>
            <div class="font-medium text-indigo-100">Email</div>
            <a href="mailto:${businessEmail}" class="text-white hover:text-indigo-200 transition-colors">
              ${businessEmail}
            </a>
          </div>
        </div>
      `
    }
    
    // Website
    if (website) {
      const safeHref = this.safeUrl(website)
      const displayUrl = this.escapeHtml(website.replace(/^https?:\/\//, ''))
      const linkInner = `${displayUrl}
              <i class="fas fa-external-link-alt text-xs ml-1"></i>`
      contactHtml += `
        <div class="flex items-center">
          <div class="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-4">
            <i class="fas fa-globe text-sm"></i>
          </div>
          <div>
            <div class="font-medium text-indigo-100">Website</div>
            ${safeHref
              ? `<a href="${safeHref}" target="_blank" rel="noopener noreferrer"
               class="text-white hover:text-indigo-200 transition-colors">
              ${linkInner}
            </a>`
              : `<span class="text-white">${linkInner}</span>`}
          </div>
        </div>
      `
    }
    
    contactHtml += '</div>'
    
    // Update the display
    this.displayTarget.innerHTML = contactHtml
  }

  updateBusinessDetails() {
    // Get current form data
    const form = this.formTarget.querySelector('form')
    const formData = new FormData(form)
    
    // Extract values
    const foundedYear = formData.get('organization[founded_year]') || ''
    const employeeCount = formData.get('organization[employee_count]') || ''
    const businessLicense = formData.get('organization[business_license]') || ''
    const businessHours = formData.get('organization[business_hours]') || ''
    
    // Build the HTML structure
    let businessHtml = '<div class="space-y-4">'
    
    // Founded Year
    if (foundedYear) {
      businessHtml += `
        <div class="flex justify-between items-center py-3 border-b border-gray-100">
          <span class="font-medium text-gray-700">Founded</span>
          <span class="text-gray-900 font-semibold">${foundedYear}</span>
        </div>
      `
      
      // Calculate years in business
      const currentYear = new Date().getFullYear()
      const yearsInBusiness = currentYear - parseInt(foundedYear)
      if (yearsInBusiness > 0) {
        businessHtml += `
          <div class="flex justify-between items-center py-3 border-b border-gray-100">
            <span class="font-medium text-gray-700">Years in Business</span>
            <span class="text-gray-900 font-semibold">${yearsInBusiness}+ years</span>
          </div>
        `
      }
    }
    
    // Employee Count
    if (employeeCount) {
      const formattedCount = parseInt(employeeCount).toLocaleString()
      businessHtml += `
        <div class="flex justify-between items-center py-3 border-b border-gray-100">
          <span class="font-medium text-gray-700">Team Size</span>
          <span class="text-gray-900 font-semibold">${formattedCount} employees</span>
        </div>
      `
    }
    
    // Business License
    if (businessLicense) {
      businessHtml += `
        <div class="flex justify-between items-center py-3 ${businessHours ? 'border-b border-gray-100' : ''}">
          <span class="font-medium text-gray-700">Business License</span>
          <span class="text-gray-900 font-semibold">${businessLicense}</span>
        </div>
      `
    }
    
    // Business Hours
    if (businessHours) {
      businessHtml += `
        <div class="flex justify-between items-center py-3">
          <span class="font-medium text-gray-700">Business Hours</span>
          <span class="text-gray-900 font-semibold">${businessHours}</span>
        </div>
      `
    }
    
    businessHtml += '</div>'
    
    // Update the display
    this.displayTarget.innerHTML = businessHtml
  }

  updateSocialLinks() {
    // Get current form data
    const form = this.formTarget.querySelector('form')
    const formData = new FormData(form)
    
    // Extract social media links - handle the Rails parameter structure
    const socialLinks = {}
    for (let [key, value] of formData.entries()) {
      // Look for the nested Rails parameter structure: organization[social_links_hash][platform]
      if (key.includes('organization[social_links_hash]') && value.trim()) {
        const match = key.match(/organization\[social_links_hash\]\[(\w+)\]/)
        if (match) {
          const platform = match[1]
          socialLinks[platform] = value
        }
      }
    }
    
    // Build the HTML structure
    let socialHtml = ''
    
    if (Object.keys(socialLinks).length > 0) {
      socialHtml = '<div class="grid grid-cols-2 gap-4">'
      
      for (let [platform, url] of Object.entries(socialLinks)) {
        const safeHref = this.safeUrl(url)
        if (!safeHref) continue
        const displayUrl = this.escapeHtml(String(url).replace(/^https?:\/\//, ''))
        const platformClass = String(platform).toLowerCase().replace(/[^a-z0-9-]/g, '')
        const platformLabel = this.escapeHtml(platform)
        socialHtml += `
          <a href="${safeHref}" target="_blank" rel="noopener noreferrer"
             class="flex items-center p-3 bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 group">
            <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3 group-hover:bg-blue-100 transition-colors">
              <i class="fab fa-${platformClass} text-gray-600 group-hover:text-blue-600"></i>
            </div>
            <div class="flex-1">
              <span class="font-medium text-gray-900 capitalize text-sm">${platformLabel}</span>
              <div class="text-xs text-gray-500 truncate">${displayUrl}</div>
            </div>
            <i class="fas fa-external-link-alt text-xs text-gray-400 group-hover:text-blue-600"></i>
          </a>
        `
      }
      
      socialHtml += '</div>'
    } else {
      socialHtml = `
        <div class="text-center py-6">
          <div class="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center mx-auto mb-3">
            <i class="fas fa-share-alt text-blue-600"></i>
          </div>
          <p class="text-gray-600 mb-3">No social media links yet</p>
        </div>
      `
    }
    
    // Update the display
    this.displayTarget.innerHTML = socialHtml
  }

  updateProfileCompletion(completionPercentage) {
    // Find the profile completion elements
    const percentageElement = document.getElementById('completion-percentage')
    const progressBar = document.getElementById('completion-progress-bar')
    const messageElement = document.getElementById('completion-message')
    
    // Update completion percentage if element exists
    if (percentageElement && completionPercentage !== undefined) {
      percentageElement.textContent = `${completionPercentage}%`
    }
    
    // Update progress bar width if element exists
    if (progressBar && completionPercentage !== undefined) {
      progressBar.style.width = `${completionPercentage}%`
    }
    
    // Update or hide/show the completion message
    if (messageElement && completionPercentage !== undefined) {
      if (completionPercentage < 80) {
        messageElement.textContent = 'Complete your profile to increase visibility and credibility'
        messageElement.classList.remove('hidden')
      } else {
        messageElement.classList.add('hidden')
      }
    } else if (completionPercentage >= 80) {
      // If message element doesn't exist but completion is >= 80, we might need to remove it
      const existingMessage = document.querySelector('#profile-completion-section .text-xs.text-gray-600')
      if (existingMessage) {
        existingMessage.remove()
      }
    }
    
    // Add a subtle animation effect to indicate the update
    const completionSection = document.getElementById('profile-completion-section')
    if (completionSection) {
      completionSection.classList.add('bg-green-50', 'border-green-200')
      setTimeout(() => {
        completionSection.classList.remove('bg-green-50', 'border-green-200')
      }, 2000)
    }
  }

  showNotification(message, type = 'info') {
    // Create and show a toast notification
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg transition-all duration-300 transform translate-x-full ${
      type === 'success' ? 'bg-green-500 text-white' :
      type === 'error' ? 'bg-red-500 text-white' :
      'bg-blue-500 text-white'
    }`
    notification.textContent = message
    
    document.body.appendChild(notification)
    
    // Slide in
    setTimeout(() => {
      notification.classList.remove('translate-x-full')
    }, 10)
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.classList.add('translate-x-full')
      setTimeout(() => {
        document.body.removeChild(notification)
      }, 300)
    }, 3000)
  }

  // Escape for safe interpolation into text and double-quoted attributes.
  escapeHtml(text) {
    return String(text == null ? "" : text).replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ))
  }

  // Allow only safe http(s)/mailto/relative URLs; reject javascript: etc.
  safeUrl(url) {
    if (!url) return ""
    const raw = String(url).trim()
    if (/^(https?:\/\/|mailto:|\/)/i.test(raw)) {
      return raw.replace(/"/g, "%22").replace(/</g, "%3C").replace(/>/g, "%3E")
    }
    return ""
  }
}